import React, { useState, useEffect, useCallback } from 'react';

// Main CyberCharlie Component - Full screen animated mascot
const CyberCharlie = ({
  messages,
  onComplete,
  position = 'right',
  autoStart = true,
  showSkip = true,
}) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isVisible, setIsVisible] = useState(autoStart);
  const [isWaving, setIsWaving] = useState(false);
  const [isBlinking, setIsBlinking] = useState(false);
  const [showBubble, setShowBubble] = useState(false);

  // Blinking animation
  useEffect(() => {
    const blinkInterval = setInterval(() => {
      setIsBlinking(true);
      setTimeout(() => setIsBlinking(false), 150);
    }, 3000);
    return () => clearInterval(blinkInterval);
  }, []);

  // Wave animation on mount
  useEffect(() => {
    if (isVisible) {
      setIsWaving(true);
      const timer = setTimeout(() => {
        setIsWaving(false);
        setShowBubble(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  // Typewriter effect
  useEffect(() => {
    if (!showBubble || currentMessageIndex >= messages.length) return;

    const currentMessage = messages[currentMessageIndex];
    setIsTyping(true);
    setDisplayedText('');

    let charIndex = 0;
    const typeInterval = setInterval(() => {
      if (charIndex < currentMessage.text.length) {
        setDisplayedText(currentMessage.text.slice(0, charIndex + 1));
        charIndex++;
      } else {
        clearInterval(typeInterval);
        setIsTyping(false);
      }
    }, 30);

    return () => clearInterval(typeInterval);
  }, [currentMessageIndex, messages, showBubble]);

  const handleNext = useCallback(() => {
    if (currentMessageIndex < messages.length - 1) {
      setCurrentMessageIndex((prev) => prev + 1);
    } else {
      onComplete?.();
    }
  }, [currentMessageIndex, messages.length, onComplete]);

  const handleSkip = useCallback(() => {
    onComplete?.();
  }, [onComplete]);

  if (!isVisible) return null;

  return (
    <>
      {/* Dark overlay backdrop */}
      <div
        onClick={handleSkip}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          zIndex: 40,
        }}
      />

      {/* Centered Charlie container */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px', maxWidth: '672px' }}>
          {/* Charlie Avatar */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            {/* Large glow effect */}
            <div
              style={{
                position: 'absolute',
                top: '-20px',
                left: '-20px',
                right: '-20px',
                bottom: '-20px',
                backgroundColor: 'rgba(34, 197, 94, 0.3)',
                borderRadius: '50%',
                filter: 'blur(32px)',
                animation: 'pulse 2s infinite',
              }}
            />

            {/* Character container */}
            <div
              style={{
                position: 'relative',
                width: '128px',
                height: '128px',
                cursor: 'pointer',
                transition: 'transform 0.3s',
                animation: isWaving ? 'bounce 0.5s ease-in-out' : 'none',
              }}
            >
              {/* Robot body SVG */}
              <svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%', filter: 'drop-shadow(0 10px 20px rgba(0, 0, 0, 0.3))' }}>
                <defs>
                  <radialGradient id="cyberGlow" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#22c55e" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
                  </radialGradient>
                  <linearGradient id="bodyGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#1e293b" />
                    <stop offset="100%" stopColor="#0f172a" />
                  </linearGradient>
                  <linearGradient id="screenGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#064e3b" />
                    <stop offset="100%" stopColor="#022c22" />
                  </linearGradient>
                </defs>

                {/* Outer glow */}
                <circle cx="50" cy="50" r="48" fill="url(#cyberGlow)" />

                {/* Main body */}
                <rect x="20" y="25" width="60" height="55" rx="12" fill="url(#bodyGradient)" stroke="#22c55e" strokeWidth="2" />

                {/* Screen/face */}
                <rect x="26" y="31" width="48" height="35" rx="6" fill="url(#screenGradient)" />

                {/* Scan line effect */}
                <rect x="26" y="31" width="48" height="2" fill="#22c55e" opacity="0.1">
                  <animate attributeName="y" values="31;64;31" dur="3s" repeatCount="indefinite" />
                </rect>

                {/* Eyes */}
                {isBlinking ? (
                  <>
                    <rect x="32" y="44" width="12" height="2" rx="1" fill="#22c55e" />
                    <rect x="56" y="44" width="12" height="2" rx="1" fill="#22c55e" />
                  </>
                ) : (
                  <>
                    <circle cx="38" cy="45" r="6" fill="#22c55e">
                      <animate attributeName="opacity" values="1;0.7;1" dur="2s" repeatCount="indefinite" />
                    </circle>
                    <circle cx="38" cy="45" r="3" fill="#4ade80" />
                    <circle cx="62" cy="45" r="6" fill="#22c55e">
                      <animate attributeName="opacity" values="1;0.7;1" dur="2s" repeatCount="indefinite" />
                    </circle>
                    <circle cx="62" cy="45" r="3" fill="#4ade80" />
                  </>
                )}

                {/* Mouth - happy expression */}
                <path d="M 42 54 Q 50 60 58 54" stroke="#22c55e" strokeWidth="2" fill="none" strokeLinecap="round" />

                {/* Antenna */}
                <line x1="50" y1="25" x2="50" y2="15" stroke="#22c55e" strokeWidth="2" />
                <circle cx="50" cy="12" r="4" fill="#22c55e">
                  <animate attributeName="opacity" values="1;0.3;1" dur="1s" repeatCount="indefinite" />
                </circle>

                {/* Ear pieces */}
                <rect x="14" y="40" width="6" height="15" rx="2" fill="#1e293b" stroke="#22c55e" strokeWidth="1" />
                <rect x="80" y="40" width="6" height="15" rx="2" fill="#1e293b" stroke="#22c55e" strokeWidth="1" />

                {/* Data streams on body */}
                <text x="28" y="76" fill="#22c55e" fontSize="4" opacity="0.5" fontFamily="monospace">
                  01100011
                </text>
              </svg>

              {/* Floating particles */}
              <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: '8px', left: '8px', width: '8px', height: '8px', backgroundColor: '#4ade80', borderRadius: '50%', animation: 'float 3s ease-in-out infinite' }} />
                <div style={{ position: 'absolute', top: '16px', right: '12px', width: '8px', height: '8px', backgroundColor: '#4ade80', borderRadius: '50%', animation: 'float 3s ease-in-out infinite 0.5s' }} />
                <div style={{ position: 'absolute', bottom: '12px', left: '16px', width: '8px', height: '8px', backgroundColor: '#4ade80', borderRadius: '50%', animation: 'float 3s ease-in-out infinite 1s' }} />
              </div>
            </div>

            {/* Name badge */}
            <div style={{ position: 'absolute', bottom: '-32px', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: '700',
                  color: '#4ade80',
                  backgroundColor: '#0f172a',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  border: '2px solid rgba(34, 197, 94, 0.5)',
                  boxShadow: '0 4px 12px rgba(34, 197, 94, 0.2)',
                }}
              >
                Cyber Charlie
              </span>
            </div>
          </div>

          {/* Speech Bubble */}
          {showBubble && (
            <div
              style={{
                position: 'relative',
                backgroundColor: '#1e293b',
                border: '2px solid rgba(34, 197, 94, 0.5)',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: '0 20px 40px rgba(34, 197, 94, 0.2)',
                maxWidth: '400px',
                animation: 'fadeIn 0.3s ease-out',
              }}
            >
              {/* Bubble tail */}
              <div
                style={{
                  position: 'absolute',
                  left: '-10px',
                  top: '50%',
                  transform: 'translateY(-50%) rotate(45deg)',
                  width: '16px',
                  height: '16px',
                  backgroundColor: '#1e293b',
                  borderLeft: '2px solid rgba(34, 197, 94, 0.5)',
                  borderBottom: '2px solid rgba(34, 197, 94, 0.5)',
                }}
              />

              {/* Message content */}
              <div style={{ position: 'relative', zIndex: 10 }}>
                <p style={{ color: '#f1f5f9', fontSize: '18px', lineHeight: '1.6', minHeight: '64px' }}>
                  {displayedText}
                  {isTyping && <span style={{ animation: 'blink 1s infinite', color: '#4ade80' }}>|</span>}
                </p>

                {/* Navigation buttons */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: '16px',
                    paddingTop: '16px',
                    borderTop: '1px solid #334155',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {messages.map((_, idx) => (
                      <div
                        key={idx}
                        style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: idx === currentMessageIndex ? '#4ade80' : '#475569',
                          transition: 'background-color 0.3s',
                        }}
                      />
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {showSkip && currentMessageIndex < messages.length - 1 && (
                      <button
                        onClick={handleSkip}
                        style={{
                          fontSize: '14px',
                          color: '#94a3b8',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          transition: 'color 0.2s',
                        }}
                        onMouseEnter={(e) => (e.target.style.color = '#e2e8f0')}
                        onMouseLeave={(e) => (e.target.style.color = '#94a3b8')}
                      >
                        Skip
                      </button>
                    )}
                    <button
                      onClick={handleNext}
                      disabled={isTyping}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#22c55e',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: '700',
                        borderRadius: '20px',
                        border: 'none',
                        cursor: isTyping ? 'default' : 'pointer',
                        opacity: isTyping ? 0.5 : 1,
                        transition: 'background-color 0.2s',
                        boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)',
                      }}
                      onMouseEnter={(e) => !isTyping && (e.target.style.backgroundColor = '#16a34a')}
                      onMouseLeave={(e) => (e.target.style.backgroundColor = '#22c55e')}
                    >
                      {currentMessageIndex < messages.length - 1 ? 'Next' : "Let's Go!"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0) scale(1); opacity: 0.6; }
            50% { transform: translateY(-10px) scale(1.2); opacity: 1; }
          }
          @keyframes blink {
            0%, 50%, 100% { opacity: 1; }
            25%, 75% { opacity: 0; }
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateX(-10px); }
            to { opacity: 1; transform: translateX(0); }
          }
        `}
      </style>
    </>
  );
};

// MiniCharlie Component - appears at specific steps
const MiniCharlie = ({ message, onDismiss, variant = 'green' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);

  useEffect(() => {
    // Animate in
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Typewriter effect
  useEffect(() => {
    let charIndex = 0;
    const typeInterval = setInterval(() => {
      if (charIndex < message.length) {
        setDisplayedText(message.slice(0, charIndex + 1));
        charIndex++;
      } else {
        clearInterval(typeInterval);
        setIsTyping(false);
      }
    }, 25);

    return () => clearInterval(typeInterval);
  }, [message]);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(onDismiss, 300);
  };

  // Color configurations for each variant
  const colorConfigs = {
    green: {
      border: 'rgba(34, 197, 94, 0.5)',
      bg: 'linear-gradient(135deg, rgba(20, 83, 45, 0.4) 0%, #1e293b 100%)',
      accent: '#4ade80',
      button: '#22c55e',
      buttonHover: '#16a34a',
      stroke: '#22c55e',
      glow: 'rgba(34, 197, 94, 0.3)',
    },
    orange: {
      border: 'rgba(249, 115, 22, 0.5)',
      bg: 'linear-gradient(135deg, rgba(124, 45, 18, 0.4) 0%, #1e293b 100%)',
      accent: '#fb923c',
      button: '#f97316',
      buttonHover: '#ea580c',
      stroke: '#f97316',
      glow: 'rgba(249, 115, 22, 0.3)',
    },
    purple: {
      border: 'rgba(168, 85, 247, 0.5)',
      bg: 'linear-gradient(135deg, rgba(88, 28, 135, 0.4) 0%, #1e293b 100%)',
      accent: '#c084fc',
      button: '#a855f7',
      buttonHover: '#9333ea',
      stroke: '#a855f7',
      glow: 'rgba(168, 85, 247, 0.3)',
    },
    cyan: {
      border: 'rgba(6, 182, 212, 0.5)',
      bg: 'linear-gradient(135deg, rgba(22, 78, 99, 0.4) 0%, #1e293b 100%)',
      accent: '#22d3ee',
      button: '#06b6d4',
      buttonHover: '#0891b2',
      stroke: '#06b6d4',
      glow: 'rgba(6, 182, 212, 0.3)',
    },
    // New DevSecAI project colors
    ice: {
      border: 'rgba(56, 189, 248, 0.5)',
      bg: 'linear-gradient(135deg, rgba(12, 74, 110, 0.4) 0%, #1e293b 100%)',
      accent: '#7dd3fc',
      button: '#0ea5e9',
      buttonHover: '#0284c7',
      stroke: '#0ea5e9',
      glow: 'rgba(56, 189, 248, 0.3)',
    },
    amber: {
      border: 'rgba(245, 158, 11, 0.5)',
      bg: 'linear-gradient(135deg, rgba(120, 53, 15, 0.4) 0%, #1e293b 100%)',
      accent: '#fcd34d',
      button: '#f59e0b',
      buttonHover: '#d97706',
      stroke: '#f59e0b',
      glow: 'rgba(245, 158, 11, 0.3)',
    },
    rose: {
      border: 'rgba(244, 63, 94, 0.5)',
      bg: 'linear-gradient(135deg, rgba(136, 19, 55, 0.4) 0%, #1e293b 100%)',
      accent: '#fb7185',
      button: '#f43f5e',
      buttonHover: '#e11d48',
      stroke: '#f43f5e',
      glow: 'rgba(244, 63, 94, 0.3)',
    },
    lime: {
      border: 'rgba(132, 204, 22, 0.5)',
      bg: 'linear-gradient(135deg, rgba(63, 98, 18, 0.4) 0%, #1e293b 100%)',
      accent: '#bef264',
      button: '#84cc16',
      buttonHover: '#65a30d',
      stroke: '#84cc16',
      glow: 'rgba(132, 204, 22, 0.3)',
    },
    indigo: {
      border: 'rgba(129, 140, 248, 0.5)',
      bg: 'linear-gradient(135deg, rgba(49, 46, 129, 0.4) 0%, #1e293b 100%)',
      accent: '#a5b4fc',
      button: '#6366f1',
      buttonHover: '#4f46e5',
      stroke: '#6366f1',
      glow: 'rgba(129, 140, 248, 0.3)',
    },
    red: {
      border: 'rgba(239, 68, 68, 0.5)',
      bg: 'linear-gradient(135deg, rgba(127, 29, 29, 0.4) 0%, #1e293b 100%)',
      accent: '#fca5a5',
      button: '#ef4444',
      buttonHover: '#dc2626',
      stroke: '#ef4444',
      glow: 'rgba(239, 68, 68, 0.3)',
    },
  };

  const colors = colorConfigs[variant] || colorConfigs.green;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 50,
        maxWidth: '400px',
        transition: 'all 0.3s ease-out',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
      }}
    >
      <div
        style={{
          borderRadius: '12px',
          border: `2px solid ${colors.border}`,
          padding: '16px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
          background: colors.bg,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          {/* Mini Charlie Avatar */}
          <div style={{ flexShrink: 0, width: '48px', height: '48px', position: 'relative' }}>
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backgroundColor: colors.glow,
                borderRadius: '50%',
                filter: 'blur(12px)',
                animation: 'pulse 2s infinite',
              }}
            />
            <svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%', filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))', position: 'relative' }}>
              <defs>
                <linearGradient id="miniBodyGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#1e293b" />
                  <stop offset="100%" stopColor="#0f172a" />
                </linearGradient>
              </defs>
              <rect x="20" y="25" width="60" height="55" rx="12" fill="url(#miniBodyGradient)" stroke={colors.stroke} strokeWidth="3" />
              <circle cx="38" cy="45" r="6" fill={colors.stroke}>
                <animate attributeName="opacity" values="1;0.7;1" dur="2s" repeatCount="indefinite" />
              </circle>
              <circle cx="62" cy="45" r="6" fill={colors.stroke}>
                <animate attributeName="opacity" values="1;0.7;1" dur="2s" repeatCount="indefinite" />
              </circle>
              <path d="M 42 56 Q 50 62 58 56" stroke={colors.stroke} strokeWidth="3" fill="none" strokeLinecap="round" />
              <line x1="50" y1="25" x2="50" y2="15" stroke={colors.stroke} strokeWidth="2" />
              <circle cx="50" cy="12" r="4" fill={colors.stroke}>
                <animate attributeName="opacity" values="1;0.3;1" dur="1s" repeatCount="indefinite" />
              </circle>
            </svg>
          </div>

          {/* Message */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '12px', fontWeight: '700', color: colors.accent, marginBottom: '4px' }}>
              Cyber Charlie says...
            </div>
            <p style={{ color: '#e2e8f0', fontSize: '14px', lineHeight: '1.5' }}>
              {displayedText}
              {isTyping && <span style={{ animation: 'blink 1s infinite', color: colors.accent }}>|</span>}
            </p>
          </div>

          {/* Close button */}
          <button
            onClick={handleDismiss}
            style={{
              flexShrink: 0,
              color: '#94a3b8',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => (e.target.style.color = 'white')}
            onMouseLeave={(e) => (e.target.style.color = '#94a3b8')}
          >
            <svg style={{ width: '20px', height: '20px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Got it button */}
        <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleDismiss}
            style={{
              padding: '8px 16px',
              backgroundColor: colors.button,
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              borderRadius: '20px',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = colors.buttonHover)}
            onMouseLeave={(e) => (e.target.style.backgroundColor = colors.button)}
          >
            Got it!
          </button>
        </div>
      </div>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          @keyframes blink {
            0%, 50%, 100% { opacity: 1; }
            25%, 75% { opacity: 0; }
          }
        `}
      </style>
    </div>
  );
};

// Project-specific intro messages
export const project1IntroMessages = [
  {
    text: "Welcome, Security Engineer! You've just been hired by NexusCore Technologies - a Series A fintech startup processing $2M in daily transactions.",
  },
  {
    text: "NexusCore's CTO Sarah Chen is worried. They've had 3 security incidents this quarter, and investors are demanding better security practices before the next funding round.",
  },
  {
    text: "Your mission: Implement a complete DevSecOps pipeline using GitHub Actions. You'll set up SAST with CodeQL, dependency scanning with Trivy, secret detection with Gitleaks, and IaC security scanning.",
  },
  {
    text: "This is REAL work that security engineers do every day. When you're done, you'll have a professional portfolio project that shows employers you can secure production systems!",
  },
  {
    text: "Your GitHub repo will include a professional README, architecture diagrams, and documented security findings. This will look AMAZING on your CV. Let's build something impressive!",
  },
];

export const project2IntroMessages = [
  {
    text: "Outstanding work on Project 1! NexusCore's board was so impressed, they've promoted you to Lead Security Engineer. Time for a bigger challenge!",
  },
  {
    text: "NexusCore just acquired 'DataFlow Analytics' - a company running on GitLab and Azure. The acquisition due diligence revealed ZERO security scanning in their pipelines.",
  },
  {
    text: "Your mission: Build an enterprise-grade DevSecOps pipeline using GitLab CI/CD and Azure. You'll implement Semgrep for SAST, Snyk for SCA, Checkov for IaC security, and GitLab's secret detection.",
  },
  {
    text: "This project showcases your versatility - GitHub AND GitLab, AWS AND Azure. Employers specifically look for engineers who can work across multiple platforms!",
  },
  {
    text: "By the end, you'll have TWO professional portfolio projects demonstrating real-world security engineering. That's exactly what hiring managers want to see. Let's do this!",
  },
];

export const project3IntroMessages = [
  {
    text: "Welcome to the Threat Modelling Workshop! I'm Cyber Charlie, and today we're thinking like attackers to protect defenders!",
  },
  {
    text: "You've just been hired as Security Architect at Solaris Care Connect 360 - a healthcare platform with 50,000 patients and critical medical data.",
  },
  {
    text: "Healthcare data is gold to hackers - worth 10x more than credit cards on the dark web! The stakes couldn't be higher.",
  },
  {
    text: "Your mission: Build a comprehensive threat model using STRIDE methodology, map attacks to MITRE ATT&CK, and analyze threats through the Cyber Kill Chain.",
  },
  {
    text: "By the end, you'll have a professional threat model document that demonstrates real security architecture skills. Let's think like hackers to defend like pros!",
  },
];

export const project4IntroMessages = [
  {
    text: "Welcome to the Kubernetes Security challenge! I'm Cyber Charlie, and today we're securing cloud-native infrastructure!",
  },
  {
    text: "CloudNative Finance is deploying their trading platform on Google Kubernetes Engine. But containers bring new attack surfaces!",
  },
  {
    text: "Kubernetes is everywhere - 96% of organizations use or evaluate it. K8s security skills are in MASSIVE demand!",
  },
  {
    text: "Your mission: Implement defense in depth with Trivy for image scanning, Falco for runtime detection, and OPA for policy enforcement.",
  },
  {
    text: "By the end, you'll have hands-on experience securing real cloud infrastructure. This is ADVANCED DevSecOps in action!",
  },
];

// Step-specific messages for Project 1
export const project1StepMessages = {
  2: { text: "Great job creating your repo! This is the foundation of your DevSecOps portfolio. Employers LOVE seeing active GitHub profiles!" },
  5: { text: "Look at that project structure! You're building something real. Every professional security engineer uses this exact folder layout." },
  6: { text: "These vulnerabilities look scary, but that's the point! Real security engineers find these every day. You're learning what hackers look for." },
  9: { text: "This GitHub Actions workflow is the heart of DevSecOps! Automated security scanning is what separates good companies from breached ones." },
  10: { text: "You did it! You just built something that companies pay consultants $10,000+ to implement. Put this on your CV with pride!" },
};

// Step-specific messages for Project 2
export const project2StepMessages = {
  2: { text: "GitLab project created! Now you're learning both major DevOps platforms. That's exactly what makes you hireable!" },
  5: { text: "Azure infrastructure coming up! Learning multiple clouds (AWS AND Azure) puts you ahead of 90% of candidates." },
  8: { text: "Checkov has over 1000 security policies! It's like having a security expert review your Terraform code 24/7." },
  10: { text: "TWO complete DevSecOps projects! GitHub + AWS and GitLab + Azure. You've just proven you can work anywhere. Incredible work!" },
};

// Step-specific messages for Project 3
export const project3StepMessages = {
  2: { text: "Architecture diagrams are the foundation of threat modelling. You can't protect what you don't understand! Great start!" },
  3: { text: "Data Flow Diagrams reveal how data moves through your system. Attackers LOVE finding unprotected data paths!" },
  4: { text: "STRIDE is used by Microsoft and top security teams worldwide. You're learning industry-standard methodology!" },
  6: { text: "MITRE ATT&CK is the gold standard for understanding adversary behavior. This knowledge is incredibly valuable!" },
  7: { text: "The Kill Chain shows how attacks progress stage by stage. Understanding this helps you build defense in depth!" },
  8: { text: "Risk scoring helps prioritize what to fix first. Security teams can't fix everything - smart prioritization is key!" },
  11: { text: "Attack trees help visualize complex attack paths. This is advanced security thinking!" },
  12: { text: "You've created a professional threat model! This is exactly what security architects deliver to executives. AMAZING work!" },
};

// Step-specific messages for Project 4
export const project4StepMessages = {
  2: { text: "GCP setup complete! Google Cloud Platform powers some of the world's largest applications. You're in good company!" },
  3: { text: "Your own GKE cluster! This is real cloud infrastructure. Everything you learn here applies to production environments!" },
  4: { text: "These vulnerable workloads teach you what attackers exploit. Understanding weaknesses is the first step to fixing them!" },
  5: { text: "Trivy is used by thousands of companies. Finding vulnerabilities BEFORE deployment is the DevSecOps way!" },
  7: { text: "Falco is the CNCF runtime security project. Detecting attacks in real-time is your last line of defense!" },
  8: { text: "You just triggered real security alerts! This is what SOC analysts see when attacks happen in production!" },
  10: { text: "OPA and Gatekeeper enforce policy-as-code. This is GitOps security at its finest!" },
  12: { text: "Network Policies are like firewalls for your pods. Zero-trust networking starts here!" },
  14: { text: "INCREDIBLE! You've secured a complete Kubernetes cluster with industry-standard tools. This is advanced-level work!" },
};

// Encouragement messages that appear randomly
export const encouragementMessages = [
  { text: "You're doing great! Security engineers need patience, and you've got it!" },
  { text: "Pro tip: Every error is a learning opportunity. Even senior engineers Google things daily!" },
  { text: "Fun fact: Companies pay $100k+ for engineers who can do what you're learning right now!" },
  { text: "Almost there! The best security engineers started exactly where you are." },
  { text: "Remember: Every line of secure code you write protects real people. You're a digital guardian!" },
];

// ============================================================
// DevSecAI CONSULTING PROJECTS - Intro Messages
// ============================================================

// Project 5: Snowflake Security
export const snowflakeSecurityIntroMessages = [
  {
    text: "Welcome to DevSecAI, the world's leading AI security consultancy! I'm Cyber Charlie, your guide through this engagement.",
  },
  {
    text: "You've just joined DevSecAI as a Data Security Consultant. Your first client? FinanceFlow Inc. - a fintech processing $50 billion in annual transactions.",
  },
  {
    text: "FinanceFlow's CISO is concerned. Their Snowflake data warehouse contains customer PII, transaction histories, and financial analytics. A breach could be catastrophic.",
  },
  {
    text: "Your mission: Implement enterprise-grade security controls including RBAC, dynamic data masking, row-level security, and comprehensive audit logging.",
  },
  {
    text: "Complete this engagement successfully, and you'll earn your DevSecAI Data Security certification. This is REAL consultant work. Let's secure some data!",
  },
];

// Project 6: Databricks Security
export const databricksSecurityIntroMessages = [
  {
    text: "Welcome back to DevSecAI! Your success at FinanceFlow has earned you a promotion to Senior Data Security Consultant. Time for a bigger challenge!",
  },
  {
    text: "Your new client is HealthData Analytics - a healthcare analytics company processing protected health information (PHI) for 200+ hospitals.",
  },
  {
    text: "HIPAA violations can cost millions in fines. HealthData needs their Databricks lakehouse secured BEFORE their SOC 2 audit next month.",
  },
  {
    text: "Your mission: Implement Unity Catalog governance, column-level security, secrets management, and comprehensive audit trails for HIPAA compliance.",
  },
  {
    text: "DevSecAI's reputation is on the line. Show them why we're the world's leading AI security consultancy. Let's make this bulletproof!",
  },
];

// Project 7: SageMaker ML Security
export const sagemakerSecurityIntroMessages = [
  {
    text: "Exceptional work, Consultant! DevSecAI's leadership has noticed your expertise. You're being assigned to our elite ML Security Team.",
  },
  {
    text: "Your client is InsureTech AI - an insurance company using machine learning for fraud detection and risk assessment. They have 45 models in production.",
  },
  {
    text: "ML systems have unique vulnerabilities - model theft, training data poisoning, adversarial attacks. InsureTech's models make $500M in decisions annually.",
  },
  {
    text: "Your mission: Secure SageMaker end-to-end - VPC isolation, IAM hardening, model encryption, secure endpoints, and MLOps pipeline security.",
  },
  {
    text: "This is cutting-edge security work. Most consultants can't do ML security. You're building rare, high-value skills. Let's go!",
  },
];

// Project 8: Colab LLM Security (OWASP Top 10)
export const colabLlmSecurityIntroMessages = [
  {
    text: "URGENT ASSIGNMENT! DevSecAI's AI Red Team needs you. I'm Cyber Charlie, and we've got a critical engagement.",
  },
  {
    text: "LegalAI Solutions has deployed LLM-powered legal research tools to 50,000 lawyers. But they've never tested them for security vulnerabilities.",
  },
  {
    text: "Prompt injection, data exfiltration, jailbreaks - the OWASP Top 10 for LLMs outlines exactly how attackers compromise AI systems. LegalAI could be leaking privileged client data.",
  },
  {
    text: "Your mission: Red team their LLM applications using the OWASP LLM Top 10 framework. Find the vulnerabilities before attackers do.",
  },
  {
    text: "LLM security is the HOTTEST skill in cybersecurity right now. After this project, you'll be in the top 1% of security professionals. Let's hack some AI!",
  },
];

// Project 9: n8n Agent Security
export const n8nAgentSecurityIntroMessages = [
  {
    text: "Welcome to your next DevSecAI engagement! I'm Cyber Charlie, and this one involves the cutting edge of AI - autonomous agents.",
  },
  {
    text: "AutomateX Corp has deployed 500+ n8n workflows with AI agents that can execute code, call APIs, and access production databases. Zero security review.",
  },
  {
    text: "AI agents with tool access can do ANYTHING - including exfiltrating data, modifying records, or pivoting to other systems. This is a ticking time bomb.",
  },
  {
    text: "Your mission: Implement agent guardrails, secure credential management, API security, workflow access controls, and comprehensive monitoring.",
  },
  {
    text: "AI agent security is so new that there are barely any experts. Complete this, and you'll be pioneering an entirely new field. Let's set the standard!",
  },
];

// Project 10: AI Agent Incident Response
export const aiAgentIncidentResponseIntroMessages = [
  {
    text: "RED ALERT! DevSecAI's Incident Response Team has been activated. This is not a drill, Consultant.",
  },
  {
    text: "MegaCorp Financial's AI customer service agent has been compromised. It's sending customer data to external servers and making unauthorized transactions.",
  },
  {
    text: "The rogue agent has access to 2.3 million customer accounts. Every minute we delay, more data is stolen. The board is in crisis mode.",
  },
  {
    text: "Your mission: Lead the incident response - contain the breach, analyze the attack, remediate the vulnerability, and document findings for regulators.",
  },
  {
    text: "This is what separates senior consultants from juniors - handling real breaches under pressure. DevSecAI's reputation is in your hands. GO GO GO!",
  },
];

// ============================================================
// DevSecAI CONSULTING PROJECTS - Step Messages
// ============================================================

export const snowflakeSecurityStepMessages = {
  2: { text: "Your Snowflake account is set up! DevSecAI consultants always start by understanding the platform architecture." },
  4: { text: "RBAC is the foundation of data security. Every Fortune 500 company uses role-based access - you're learning enterprise skills!" },
  5: { text: "Dynamic data masking is POWERFUL. You just made it so analysts can work with data without ever seeing the actual PII. Brilliant!" },
  6: { text: "Row-level security is advanced stuff! Many data engineers struggle with this. You're ahead of the curve!" },
  8: { text: "Audit logging is crucial for compliance. When auditors ask 'who accessed what?', you'll have the answers." },
  9: { text: "Network security and encryption complete the defense-in-depth strategy. This is how DevSecAI protects clients!" },
  10: { text: "INCREDIBLE! You've secured an enterprise data warehouse. FinanceFlow's board will be thrilled. DevSecAI certification earned!" },
};

export const databricksSecurityStepMessages = {
  2: { text: "Databricks workspace ready! Unity Catalog is the game-changer for lakehouse governance. Let's master it." },
  4: { text: "Data access controls with Unity Catalog - this is the new standard for data governance. You're learning cutting-edge stuff!" },
  5: { text: "Column-level security for PHI. HIPAA compliance requires this level of granularity. Healthcare clients love this!" },
  7: { text: "Secrets management done right! Never hardcode credentials - this is Security 101 that many engineers still get wrong." },
  8: { text: "Data lineage is gold for compliance. When regulators ask 'where did this data come from?', you'll have full traceability." },
  10: { text: "HealthData Analytics is SOC 2 ready! Their auditors will breeze through the data security section. Outstanding work, Consultant!" },
};

export const sagemakerSecurityStepMessages = {
  2: { text: "AWS environment configured! SageMaker Studio is where the ML magic happens. Let's secure it." },
  4: { text: "VPC isolation for ML workloads - this prevents model theft and data exfiltration. Essential for any production ML system!" },
  5: { text: "IAM for ML is tricky - too restrictive breaks workflows, too permissive is dangerous. You're nailing the balance!" },
  7: { text: "Model encryption protects your intellectual property. These models cost millions to train - they're valuable assets!" },
  8: { text: "Secure endpoints are your last line of defense. Even if attackers get this far, they shouldn't extract anything useful." },
  9: { text: "MLOps pipeline security - securing the supply chain that produces your models. This is next-level thinking!" },
  10: { text: "Model monitoring for drift and adversarial attacks. ML systems degrade over time - continuous monitoring is essential!" },
  12: { text: "InsureTech's ML infrastructure is now bulletproof! You've just done work that few security engineers can even attempt. ELITE!" },
};

export const colabLlmSecurityStepMessages = {
  2: { text: "Lab environment ready! Time to think like an attacker. The best defenders know how hackers operate." },
  3: { text: "OWASP LLM Top 10 - this framework was just published and you're already mastering it. Bleeding edge stuff!" },
  4: { text: "Prompt injection is the #1 LLM vulnerability. You're learning to exploit AND defend against it. Dual skills!" },
  5: { text: "Insecure output handling can turn LLMs into weapons. XSS through AI is wild - and you're defending against it!" },
  7: { text: "Model DoS attacks can cost companies thousands in API fees. Rate limiting and input validation are crucial!" },
  8: { text: "Sensitive info disclosure - extracting training data from models. This attack has leaked real PII in production systems!" },
  10: { text: "LegalAI's LLMs are now OWASP-hardened! You've done what 99% of security professionals can't do. AI Red Team certified!" },
};

export const n8nAgentSecurityStepMessages = {
  2: { text: "n8n environment running! This open-source tool powers thousands of AI agent deployments. Time to secure it." },
  4: { text: "Credential management is where most breaches start. Encrypted, isolated, rotated - this is how pros do it!" },
  5: { text: "API security for AI agents is critical. Every external call is a potential attack vector. Lock it down!" },
  6: { text: "Agent guardrails - the safety barriers that prevent AI from going rogue. This is the future of AI security!" },
  7: { text: "Workflow access controls - not everyone should be able to modify AI workflows. Least privilege applies here too!" },
  9: { text: "Monitoring AI agents is crucial. If an agent starts behaving unexpectedly, you need to know IMMEDIATELY." },
  10: { text: "AutomateX's AI agents are now secure! You've pioneered agent security practices that will become industry standard. Visionary work!" },
};

export const aiAgentIncidentResponseStepMessages = {
  2: { text: "Incident declared! Your first priority is containment. Every second counts - let's isolate that rogue agent." },
  3: { text: "Agent contained! Now we need to understand HOW this happened. Forensics mode activated." },
  4: { text: "Attack chain analysis - you're tracing exactly how the attacker compromised the agent. This is detective work!" },
  5: { text: "Data exfiltration assessment - we need to know EXACTLY what was stolen for the regulatory disclosure." },
  6: { text: "Root cause identified! Now we remediate so this NEVER happens again. Patch, harden, verify." },
  7: { text: "Monitoring enhanced - if attackers try this again, we'll catch them instantly. Defense in depth!" },
  8: { text: "Incident report for regulators - clear, professional, and shows we handled this properly. Reputation preserved!" },
  9: { text: "Stakeholder communication - the board needs to know we've got this under control. Calm, confident, competent." },
  10: { text: "BREACH CONTAINED! MegaCorp's AI systems are secure. You've just handled a real-world AI incident like a pro. LEGENDARY!" },
};

export { MiniCharlie };
export default CyberCharlie;
